/**
 * Active-team selection for the current workspace.
 *
 * A user has at most one team per workspace, so the active team is the joined
 * team whose `workspace_id` matches the current workspace. The three auth entry
 * points feed the result to `setActiveTeam`, which records it as the single
 * `teamId` every analytics destination reads.
 */

/** The joined-team shape this selector needs (a subset of the API type). */
type JoinedTeamForSelection = { team_id: string; workspace_id?: string | null };

/**
 * Pick the active team for the current workspace: the joined team whose
 * `workspace_id` matches. Returns `undefined` when the user has no team in the
 * active workspace — an accepted state — so no foreign-workspace team is ever
 * selected. Workspace switches do a full reload, so this is recomputed from
 * scratch per workspace rather than reconciled in memory.
 */
export function selectActiveTeamId(
  joinedTeams: readonly JoinedTeamForSelection[] | undefined,
  workspaceId: string | null
): string | undefined {
  if (!joinedTeams?.length || workspaceId == null) return undefined;
  return joinedTeams.find((team) => team.workspace_id === workspaceId)?.team_id;
}
